import React, { useRef, useState } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import { type Instance as TippyInstance } from 'tippy.js/index';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import { ApplicationView, useController } from '@aha-app/mvc';
import WhiteboardController from '../controllers/WhiteboardController';
import { svgToIcon } from '../utils/svg';
import maskCircleSVG from '../assets/icons/maskCircle.svg';
import maskRoundedRectSVG from '../assets/icons/maskRoundedRect.svg';
import maskNoneSVG from '../assets/icons/maskNone.svg';

type MaskType = 'none' | 'circle' | 'rounded-rect';
export type { MaskType };

function maskTypeToName(maskType: MaskType) {
  switch (maskType) {
    case 'none':
      return 'Square';
    case 'circle':
      return 'Circle';
    case 'rounded-rect':
      return 'Rounded corners';
  }
}

function maskTypeToIcon(maskType: MaskType) {
  switch (maskType) {
    case 'none':
      return maskNoneSVG;
    case 'circle':
      return maskCircleSVG;
    case 'rounded-rect':
      return maskRoundedRectSVG;
  }
}

const Popup: React.FC<{
  curentMaskType: string;
  setCurrentMaskType: (maskType: MaskType) => void;
  selection: fabric.Object;
  hidePopup: () => void;
}> = ApplicationView(
  ({ curentMaskType, setCurrentMaskType, selection, hidePopup }) => {
    const controller = useController(WhiteboardController);

    const setMaskType = maskType => {
      hidePopup();
      setCurrentMaskType(maskType);
      selection.setOnObjects({
        maskType,
      });
      Tool.toolActionComplete({ controller });
    };

    return (
      <ToolbarMenu columns={3}>
        {['none', 'rounded-rect', 'circle'].map((maskType: MaskType, i) => (
          <ToolButton
            active={maskType === curentMaskType}
            key={i}
            onClick={() => setMaskType(maskType)}
            name={maskTypeToName(maskType)}
            testId={`Tool-maskType-${maskType}`}
          >
            {svgToIcon(maskTypeToIcon(maskType))}
          </ToolButton>
        ))}
      </ToolbarMenu>
    );
  }
);

class ToolImageMask extends Tool {
  static toolName = 'Mask';

  // The clipping occurs in ShaperImage->renderImageMask

  static buttonComponent() {
    const Button: ToolButtonComponent = ApplicationView(
      ({ selection, tool }) => {
        const tippyRef = useRef<TippyInstance>();
        const [curentMaskType, setCurentMaskType] = useState(
          selection.firstObject().get('maskType')
        );

        return (
          <ToolButton
            popup={
              <Popup
                curentMaskType={curentMaskType}
                setCurrentMaskType={setCurentMaskType}
                selection={selection}
                hidePopup={() => tippyRef.current?.hide()}
              />
            }
            tippyRef={tippyRef}
            id='maskType'
            tool={tool}
            active={curentMaskType !== 'none'}
          >
            {svgToIcon(maskTypeToIcon(curentMaskType))}
          </ToolButton>
        );
      }
    );

    return Button;
  }
}

export { ToolImageMask };
